import isNumber from 'lodash/isNumber';
import { createRef } from 'react';
import { CSSProperties } from 'react';
import { useMemo } from 'react';
import { useCallback } from 'react';
import React from 'react';
import { CSSTransition } from 'react-transition-group';

interface CollapsibleStyle {
    position?: CSSProperties['position'];
    transition?: CSSProperties['transition'];
}

interface Props {
    isCollapsed?: boolean;
    minHeight?: number;
    foldingTime?: number;
}

/**
 * Компонент для сворачивания блока
 *
 * @param isCollapsed Состояние свернут/развернут
 * @param isLoading Содержимое блока загружается
 * @param minHeight Минимальная высота развернутого блока
 * @param foldingTime Время сворачивания/разворачивания
 * @param children
 */
export const Collapsible: React.FC<Props> = ({
    isCollapsed = false,
    minHeight = 0,
    foldingTime = 400,
    children,
}) => {
    const ref = createRef<HTMLDivElement>();

    const setZeroHeight = useCallback(() => {
        if (ref.current) {
            ref.current.style.height = '0';
            ref.current.style.overflow = 'hidden';
        }
    }, [ref]);

    const setAutoHeight = useCallback(() => {
        if (ref.current) {
            ref.current.style.height = 'auto';
            ref.current.style.overflow = 'unset';
        }
    }, [ref]);

    const setMaxHeight = useCallback(() => {
        if (ref.current) {
            const { scrollHeight } = ref.current;
            const actualHeight = Math.max(scrollHeight, minHeight);
            ref.current.style.height = isNumber(actualHeight)
                ? `${actualHeight}px`
                : actualHeight;
        }
    }, [minHeight, ref]);

    const style = useMemo<CollapsibleStyle>(
        () => ({
            position: 'relative',
            transition: `height ${foldingTime}ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
        }),
        [foldingTime]
    );

    return (
        <CSSTransition
            in={!isCollapsed}
            timeout={foldingTime}
            mountOnEnter
            unmountOnExit
            appear
            onEnter={setZeroHeight}
            onEntering={setMaxHeight}
            onEntered={setAutoHeight}
            onExit={setMaxHeight}
            onExiting={setZeroHeight}
            nodeRef={ref}
        >
            <div ref={ref} style={style}>
                {children}
            </div>
        </CSSTransition>
    );
};
